/**
 * An org's choice of the units its numbers are shown in (GET and PUT /v1/twin/settings/units): a system, and an
 * override per dimension. The same shape and the same answers as the server's units.DisplayPrefs: `unitFor` is its
 * UnitFor, and the options per dimension are the server's units.Options (both held to
 * platform-core/internal/units/testdata/display_vectors.json).
 *
 * Storage never changes; this only says what a stored value is SHOWN in.
 */
import { dimensionOf, labelOf, systemOfUnit, type DimensionKey } from "./convert";

export type { DimensionKey } from "./convert";

export type UnitSystem = "si" | "us";

export interface DisplayPrefs {
  system: UnitSystem;
  /** One display unit per dimension, replacing the system's; a dimension left out follows the system. */
  overrides: Partial<Record<DimensionKey, string>>;
}

/** The dimensions a display unit may be chosen for, in the order the settings form shows them. */
export const DIMENSIONS: readonly DimensionKey[] = ["°C", "Δ°C", "Pa", "kg", "mm", "ml", "L/min", "s"];

/** Each dimension in words. */
export const DIMENSION_NAMES: Record<DimensionKey, string> = {
  "°C": "Temperature",
  "Δ°C": "Temperature difference",
  Pa: "Pressure",
  kg: "Mass",
  mm: "Length",
  ml: "Volume",
  "L/min": "Flow",
  s: "Time",
};

/** The units a form offers per dimension, in order: the server's units.Options. */
export const UNIT_OPTIONS: Record<DimensionKey, readonly string[]> = {
  "°C": ["°C", "°F", "K"],
  "Δ°C": ["Δ°C", "Δ°F", "ΔK"],
  Pa: ["Pa", "kPa", "MPa", "bar", "mbar", "psi"],
  kg: ["kg", "g", "t", "lb", "oz"],
  mm: ["mm", "cm", "m", "in", "ft"],
  ml: ["ml", "L", "m3", "gal"],
  "L/min": ["L/min", "gal/min"],
  s: ["s", "min", "h"],
};

/**
 * What each system shows a unit of the OTHER system in, per dimension (the server's systemUnits). A system converts
 * only the other system's units: ingest keeps bar, MPa and min as the instrument reads them, so metric shows metric
 * values as recorded. Metric shows gallons in litres and gallons a minute in litres a minute; US shows litres a minute
 * in gallons a minute but keeps volumes as recorded (a millilitre of amine is no gallon: a plant that reads its water
 * in gallons sets its volume to gal). Time belongs to neither: shown as recorded.
 */
export const SYSTEM_UNITS: Record<UnitSystem, Partial<Record<DimensionKey, string>>> = {
  si: { "°C": "°C", "Δ°C": "Δ°C", Pa: "kPa", kg: "kg", mm: "mm", ml: "L", "L/min": "L/min" },
  us: { "°C": "°F", "Δ°C": "Δ°F", Pa: "psi", kg: "lb", mm: "in", "L/min": "gal/min" },
};

/** The system's name, as a person says it. */
export const SYSTEM_NAMES: Record<UnitSystem, string> = { si: "Metric", us: "US" };

/** The metric choice: US units in °C, kPa, kg, mm, L and L/min, metric values as recorded. What an org reads until an admin
 * chooses, and before the read arrives. */
export const DEFAULT_SI: DisplayPrefs = Object.freeze({ system: "si", overrides: Object.freeze({}) }) as DisplayPrefs;

/** The American choice: metric units in °F, psi, lb, in and gal/min; time and volume as recorded. */
export const DEFAULT_US: DisplayPrefs = Object.freeze({ system: "us", overrides: Object.freeze({}) }) as DisplayPrefs;

/**
 * The unit a value stored in `stored` is shown in: the override for its dimension if the org set one; else, for a unit
 * of the other system, the chosen system's unit of its dimension; else the unit as recorded, labelled (kPa for "kpa").
 * A unit the table does not know is shown exactly as stored, never guessed at. The server's UnitFor.
 */
export function unitFor(stored: string, prefs: DisplayPrefs): string {
  const dim = dimensionOf(stored);
  if (dim === "") return stored;
  // An override counts only if it names a unit of this dimension, as the server's UnitFor reads it.
  const own = prefs.overrides[dim];
  if (own !== undefined && dimensionOf(own) === dim) return labelOf(own);
  const sys = systemOfUnit(stored);
  if (sys !== "" && sys !== prefs.system) {
    const unit = SYSTEM_UNITS[prefs.system]?.[dim];
    if (unit !== undefined) return unit;
  }
  return labelOf(stored);
}

/**
 * Per dimension, keyed by its canonical unit, what a unit of the other system is shown in: the override, else the
 * system's unit, else "" where every unit is shown as recorded (the server's `resolved`).
 */
export function resolvedOf(prefs: DisplayPrefs): Record<DimensionKey, string> {
  const out = {} as Record<DimensionKey, string>;
  for (const dim of DIMENSIONS) {
    const own = prefs.overrides[dim];
    out[dim] = own !== undefined && dimensionOf(own) === dim ? labelOf(own) : (SYSTEM_UNITS[prefs.system]?.[dim] ?? "");
  }
  return out;
}

/**
 * A choice as the server sent it, read defensively: anything that is not a choice is metric, and an override that
 * names no dimension, or a unit of another dimension, is dropped rather than shown as a wrong unit. A fresh copy
 * every time, so a form that edits it never edits a default.
 */
export function prefsOf(raw: unknown): DisplayPrefs {
  const body = typeof raw === "object" && raw !== null && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  const system = body?.system;
  if (system !== "si" && system !== "us") return { system: "si", overrides: {} };
  const overrides: DisplayPrefs["overrides"] = {};
  const given = body?.overrides;
  if (typeof given === "object" && given !== null && !Array.isArray(given)) {
    for (const dim of DIMENSIONS) {
      const unit = (given as Record<string, unknown>)[dim];
      if (typeof unit === "string" && dimensionOf(unit) === dim) overrides[dim] = labelOf(unit);
    }
  }
  return { system, overrides };
}

/** Whether two choices show the same units: the same system, and the same override (or none) for each dimension. */
export function samePrefs(a: DisplayPrefs, b: DisplayPrefs): boolean {
  return a.system === b.system && DIMENSIONS.every((dim) => (a.overrides[dim] ?? "") === (b.overrides[dim] ?? ""));
}

/** The choice with another system, its overrides kept. A new object: a draft is never edited in place. */
export function withSystem(prefs: DisplayPrefs, system: UnitSystem): DisplayPrefs {
  return { system, overrides: { ...prefs.overrides } };
}

/** The choice with one dimension's override set, or removed (an empty unit follows the system). A new object. */
export function withOverride(prefs: DisplayPrefs, dim: DimensionKey, unit: string): DisplayPrefs {
  const overrides = { ...prefs.overrides };
  if (unit === "") delete overrides[dim];
  else overrides[dim] = unit;
  return { system: prefs.system, overrides };
}
